import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AdminHeader from "../../Components/AdminHeader/AdminHeader";
import AdminNav from "../../Components/AdminNav/AdminNav";
import './news.css';
import { getNewsPosts, deleteNewsPost, publishNewsPost } from "../../actions/NewsPages";
import { IMAGE_URL } from "../../path";

function StoriesList() {
    const location = useLocation();
    const navigate = useNavigate();

    if (!location?.state?.user) {
        navigate('/login');
    }

    const user = location.state?.user;
    const [isCollapsed, setIsCollapsed] = useState(true);
    const [showDropdown, setShowDropdown] = useState(false);
    const [newsPosts, setNewsPosts] = useState<any[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const postsPerPage = 10;

    const fetchNewsPosts = () => {
        getNewsPosts()
            .then(response => {
                setNewsPosts(response.data);
            })
            .catch(error => {
                console.error("Failed to fetch news stories:", error);
            });
    };

    useEffect(() => {
        fetchNewsPosts();
    }, []);

    // Pagination helpers
    const indexOfLastPost = currentPage * postsPerPage;
    const indexOfFirstPost = indexOfLastPost - postsPerPage;
    const currentPosts = newsPosts.slice(indexOfFirstPost, indexOfLastPost);
    const totalPages = Math.ceil(newsPosts.length / postsPerPage);

    const handlePageChange = (pageNumber: number) => {
        setCurrentPage(pageNumber);
    };

    const handleDelete = async (postId: number, template_id: number) => {
        if (window.confirm("Are you sure you want to delete this news story?")) {
            try {
                await deleteNewsPost(postId, template_id);
                fetchNewsPosts();
            } catch (error) {
                console.error("Failed to delete news post:", error);
                alert("Failed to delete the news story. Please try again.");
            }
        }
    };

    const handlePublish = async (postId: number, templateId: number) => {
        try {
            await publishNewsPost(postId, templateId);
            fetchNewsPosts();
        } catch (error: any) {
            console.error("Failed to publish news post:", error);

            // Check if backend returned validation errors
            if (error.response && error.response.data && error.response.data.errors) {
                const errors = error.response.data.errors;
                const messages: string[] = [];

                Object.keys(errors).forEach((key) => {
                    messages.push(`${key}: ${errors[key].join(", ")}`);
                });

                // Show alert with all missing/invalid fields
                alert("Cannot publish news story. Missing/Invalid fields:\n\n" + messages.join("\n"));
            } else {
                alert("Failed to publish the news story. Please try again.");
            }
        }
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const dropdown = document.querySelector('.dropdown-menu');
            const button = document.querySelector('.button-right');
            if (
                dropdown &&
                !dropdown.contains(event.target as Node) &&
                button &&
                !button.contains(event.target as Node)
            ) {
                setShowDropdown(false);
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    return (
        <div>
            <AdminHeader isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} user={user} />
            <AdminNav collapsed={isCollapsed} setCollapsed={setIsCollapsed} user={user} />
            <div className={`main-admin-page ${isCollapsed ? "collapsed" : "expanded"}`}>

                {/* Add News Story Dropdown */}
                <div className="content-form-button right-align" style={{ marginBottom: '15px', position: 'relative' }}>
                    <button
                        type="button"
                        className="button button-right"
                        onClick={() => setShowDropdown((prev) => !prev)}
                    >
                        Add News Story ▾
                    </button>

                    {showDropdown && (
                        <div
                            className="dropdown-menu"
                            style={{
                                position: 'absolute',
                                right: '-30px',
                                top: '100%',
                                backgroundColor: 'white',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                zIndex: 10,
                                width: '150px',
                            }}
                        >
                            {[
                                { label: 'Template 1', path: '/dashboard/news/create/template1' },
                                { label: 'Template 2', path: '/dashboard/news/create/template2' },
                                { label: 'Custom', path: '/dashboard/news/create/custom' },
                            ].map((option) => (
                                <div
                                    key={option.label}
                                    onClick={() => {
                                        navigate(option.path, { state: { user } });
                                        setShowDropdown(false);
                                    }}
                                    style={{
                                        padding: '10px 15px',
                                        cursor: 'pointer',
                                        borderBottom: '1px solid #eee',
                                    }}
                                    onMouseEnter={(e) => (e.currentTarget.style.background = '#f7f7f7')}
                                    onMouseLeave={(e) => (e.currentTarget.style.background = 'white')}
                                >
                                    {option.label}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* News Posts Table */}
                <div className="table-container">
                    <table className="news-table">
                        <thead>
                        <tr>
                            <th style={{ width: "40%" }}>Title</th>
                            <th style={{ width: "25%" }}>Cover Image</th>
                            <th style={{ width: "10%" }}>Year Posted</th>
                            <th style={{ width: "5%" }}>Template</th>
                            <th style={{ width: "20%" }}>Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        {currentPosts.map((post) => (
                            <tr key={post.id}>
                                <td>{post.title}</td>
                                <td>
                                    {post.image_path ? (
                                        <img
                                            src={IMAGE_URL + post.image_path}
                                            alt="Cover Image"
                                            style={{
                                                width: "100%",
                                                maxHeight: "120px",
                                                objectFit: "cover",
                                                borderRadius: "4px",
                                            }}
                                        />
                                    ) : (
                                        <span>No image</span>
                                    )}
                                </td>
                                <td>{post.year_posted}</td>
                                <td>{post.template_id}</td>
                                <td>
                                    {post.draft === 1 && (
                                        <button
                                            className="button small"
                                            onClick={() => handlePublish(post.id, post.template_id)}
                                            style={{ marginRight: "10px", marginBottom: '10px', backgroundColor: 'green' }}
                                        >
                                            Publish
                                        </button>
                                    )}
                                    <button
                                        className="button small"
                                        onClick={() => window.open(`/news/${post.search_title}`, '_blank')}
                                        style={{ marginRight: "10px", marginBottom: '10px' }}
                                    >
                                        Preview
                                    </button>
                                    <button
                                        className="button small"
                                        onClick={() => navigate(`/dashboard/news/${post.template_id}/${post.id}/edit`, { state: { user } })}
                                        style={{ marginRight: "10px", marginBottom: '10px' }}
                                    >
                                        View / Edit
                                    </button>
                                    {post.template_id === 3 && (
                                        <button
                                            className="button small"
                                            onClick={() => navigate(`/dashboard/news/edit-order/${post.id}`, { state: { user } })}
                                            style={{ marginRight: "10px", marginBottom: '10px' }}
                                        >
                                            Edit Order
                                        </button>
                                    )}
                                    <button
                                        className="button small danger"
                                        onClick={() => handleDelete(post.id, post.template_id)}
                                        style={{ color: "black" }}
                                    >
                                        Delete
                                    </button>
                                </td>

                            </tr>
                        ))}
                        </tbody>
                    </table>

                    {/* Pagination Controls */}
                    <div className="pagination-controls">
                        {Array.from({ length: totalPages }, (_, index) => index + 1).map((pageNumber) => (
                            <button
                                key={pageNumber}
                                className={`pagination-button ${currentPage === pageNumber ? "active" : ""}`}
                                onClick={() => handlePageChange(pageNumber)}
                            >
                                {pageNumber}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default StoriesList;
